import { CarouselProps } from '@design-system/carousel';
import {
  ExtendableImage,
  ExtendableImageProps,
} from '@design-system/extendable-image';
import { ProductPageHeaderProps } from '@design-system/product-page-header';
import { VariantItemProps } from '@design-system/product-page-header/dist/types/models';
import { QuantityButtonProps } from '@design-system/quantity-button';
import { navigate } from 'gatsby';
import uniq from 'lodash/uniq';
import React, { ReactElement } from 'react';

import ShopifyBlock from '~/components/ShopifyBlock';
import { AddToCartModalProps } from '~/components/ShopifyBlock/NotificationModal/models';
import TMPLinkWrap from '~/components/TMPLinkWrap';
import { buttonMapper } from '~/mappers/button';
import { carouselMapper } from '~/mappers/carousel';
import { iconMapper } from '~/mappers/common';
import { imageMapper } from '~/mappers/image';
import { breadcrumbsMapper } from '~/mappers/pageHeader';
import { quantityButtonMapper } from '~/mappers/quantityButton';
import { retailersMapper } from '~/mappers/retailers';
import { ProductPage, Variants } from '~/templates/L2ProductPage/models';
import { SalsifyAssets, SalsifyProductData } from '~/types/common';
import {
  nlToBr,
  snakeToCamelWithSpecialCases,
  validateTextLength,
} from '~/utils/stringUtils/stringUtils';

import { ProductPageHeader, ShopifyBlockProps } from './models';

const extractFieldOptions = (
  variants: Pick<ProductPage, 'fields'>[],
  fieldsName: string[]
): VariantItemProps[] => {
  const validVariants = variants.filter(
    (variant) => variant.fields.productData
  );

  const dim2Variants = fieldsName[1]
    ? uniq(
        validVariants.map(
          (variant) => variant.fields.productData[fieldsName[1]]
        )
      )
    : [];

  return validVariants.map((variant) => ({
    dimension1: variant.fields.productData[fieldsName[0]],
    ...(dim2Variants.length > 1 && {
      dimension2: variant.fields.productData[fieldsName[1]],
    }),
    href: variant.fields.fullPath,
  }));
};

const moveImageToBeginning = (
  images: SalsifyAssets[],
  imageId: string
): SalsifyAssets[] => {
  if (!imageId) {
    return images;
  }

  return images.slice().sort(({ salsifyID: idA }, { salsifyID: idB }) => {
    if (idA === imageId) return -1;
    if (idB === imageId) return 1;

    return 0;
  });
};

const getAlternateImages = (
  images: SalsifyAssets[],
  imageIds: string[]
): SalsifyAssets[] =>
  images.filter((image) => imageIds.includes(image.salsifyID));

const imageElementsMapper = (images: SalsifyAssets[]): ReactElement[] =>
  images.map((image, index) => (
    <ExtendableImage
      key={image.salsifyID}
      {...(imageMapper(
        {
          image: {
            gatsbyImageData: image.localAsset.childImageSharp?.gatsbyImageData,
            url: image.salsifyUrl,
          },
          alt: image.altText,
          objectFit: 'contain',
        },
        index === 0
      ) as ExtendableImageProps)}
    />
  ));

const variantSelectorMapper = ({
  variantSelector,
  productData,
}: {
  variantSelector: Variants;
  productData: SalsifyProductData;
}) => {
  const {
    labels: [{ dimensions }],
    variants,
  } = variantSelector;

  return {
    currentDimension1: productData[dimensions[0].dimension],
    currentDimension2: productData[dimensions[1]?.dimension],
    labelDimension1: dimensions[0].label,
    labelDimension2: dimensions[1]?.label,
    variants: extractFieldOptions(
      variants,
      dimensions.map(({ dimension }) => dimension)
    ),
  };
};

const shopifyBlockMapper = ({
  addToCartModal,
  addToCartButton,
  buyNowButton,
  quantity,
  currentPrice,
  currentPriceUI,
  discountedPrice,
  discountedPriceUI,
  availableForSale,
  productData,
  availableQuantity,
  inventoryQuantity,
}: ShopifyBlockProps) => {
  if (
    !addToCartModal ||
    !addToCartButton ||
    !quantity ||
    !currentPrice ||
    !currentPriceUI
  )
    return undefined;

  const {
    descriptionShort,
    productTitle,
    salsifyDigitalAssets,
    aromaScent,
    netContentStatement,
  } = productData;

  return (
    <ShopifyBlock
      isProductPageHeader
      availableForSale={!!availableForSale}
      addToCartButton={{
        ...buttonMapper({
          ...addToCartButton,
          text: validateTextLength(addToCartButton),
        }),
        storefrontId: addToCartButton.storefrontId,
      }}
      buyNowButton={
        buyNowButton &&
        buttonMapper({
          ...buyNowButton,
          text: validateTextLength(buyNowButton),
        })
      }
      addToCartModal={
        {
          ...snakeToCamelWithSpecialCases(addToCartModal),
          errorMessageIcon: iconMapper(addToCartModal.errorMessageIcon[0]),
          successMessageIcon: iconMapper(addToCartModal.successMessageIcon[0]),
          continueShopping: buttonMapper(addToCartModal.continueShopping),
          checkout: buttonMapper(addToCartModal.checkout),
          handleCheckoutNavigation: () => {
            navigate(addToCartModal.checkout.link.href);
          },
          productScent: aromaScent,
          productName: productTitle,
          productSize: netContentStatement,
          productImage: (
            <ExtendableImage
              {...(imageMapper({
                alt: salsifyDigitalAssets[0].altText,
                image: {
                  gatsbyImageData:
                    salsifyDigitalAssets[0].localAsset.childImageSharp
                      .gatsbyImageData,
                  url: salsifyDigitalAssets[0].salsifyUrl,
                },
                objectFit: 'contain',
              }) as ExtendableImageProps)}
            />
          ),
        } as AddToCartModalProps
      }
      currentPrice={currentPrice}
      currentPriceUI={currentPriceUI}
      discountedPrice={discountedPrice}
      discountedPriceUI={discountedPriceUI}
      quantityButton={quantityButtonMapper(quantity) as QuantityButtonProps}
      description={nlToBr(descriptionShort)}
      className="shopify-block-pdp"
      availableQuantity={availableQuantity}
      inventoryQuantity={inventoryQuantity}
    />
  );
};

export const productPageHeaderMapper = ({
  variantSelector: [variantSelector],
  headerUI: {
    breadcrumbsUI: [{ hideLastItem, navigationName, overflowButton }],
    galleryUI: [galleryUI],
    variantNameField,
  },
  fields,
  disclaimer,
  productId,
  pluginInformationText,
  isPriceSpiderAvailable,
  isAdimoAvailable,
  adimoKey,
  adimoButton,
  isMikMakAvailable,
  mikmakAppId,
  mikmakButton,
  retailers: [retailers],
  addToCartButton,
  buyNowButton,
  availableForSale,
  currentPrice,
  discountedPrice,
  currentPriceUI,
  discountedPriceUI,
  quantity,
  addToCartModal,
  availableQuantity,
  inventoryQuantity,
}: ProductPageHeader): ProductPageHeaderProps => {
  const { productData, breadcrumbs } = fields;

  const {
    descriptionShort,
    productTitle,
    salsifyDigitalAssets,
    heroImageID,
    websiteImagesID,
    barcodeNumber,
  } = productData;

  const websiteImages =
    websiteImagesID &&
    getAlternateImages(salsifyDigitalAssets, websiteImagesID);

  const imageElements = imageElementsMapper(
    websiteImagesID && websiteImagesID.length > 0
      ? moveImageToBeginning(websiteImages, heroImageID)
      : moveImageToBeginning(salsifyDigitalAssets, heroImageID)
  );

  return {
    breadcrumbs: {
      navigationName,
      hideLastItem,
      items: breadcrumbsMapper(breadcrumbs),
      overflowBtn: {
        children: overflowButton,
      },
      linkComponent: TMPLinkWrap,
    },
    productId,
    description: nlToBr(descriptionShort),
    ...(disclaimer && { disclaimer }),
    heading: nlToBr(productTitle),
    ...(retailers && {
      retailersBlock: retailersMapper(retailers, productData),
    }),
    carouselBlock: {
      ...carouselMapper<Omit<CarouselProps, 'items'>>(galleryUI),
      imageElements,
    },
    subHeading: productData[variantNameField],
    ...(variantSelector?.variants?.length > 0 && {
      variantSelector: variantSelectorMapper({ variantSelector, productData }),
    }),
    shopifyBlockComponent: shopifyBlockMapper({
      addToCartModal,
      addToCartButton,
      buyNowButton,
      quantity,
      currentPrice,
      currentPriceUI,
      discountedPrice,
      discountedPriceUI,
      availableForSale,
      productData,
      availableQuantity,
      inventoryQuantity,
    }),
    whereToBuyData: {
      barcodeNumber,
      isPriceSpiderAvailable,
      isAdimoAvailable,
      adimoKey,
      adimoButton,
      shoppableInformationText: pluginInformationText,
      isMikMakAvailable,
      mikmakAppId,
      mikmakButton,
    },
  };
};
